import React, { useEffect, useState } from "react";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import LeftArrow from "../../UI/Icons/LeftArrow";
import Delete from "../../UI/Icons/Delete";
import { useDispatch } from "react-redux";
import { AppDispatch, useAppSelector } from "../../../store";
import {
  DeleteCandidateCommentsThunk,
  getCandidateComments,
  postCandidateCommentsThunk,
} from "../../../store/superadmin/thunks";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { useFormik } from "formik";
import { isFulfilled } from "@reduxjs/toolkit";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import {
  IAdminCommentsFormInput,
  ICommentResponse,
} from "../../../store/superadmin/types";
import { hide } from "@popperjs/core";

interface AdminCommentsProps {
  candidateId: number;
  setShowAdminComments: (value: boolean) => void;
  setTotalAdminComments?: (value: number) => void;
  totalAdminComments?: number;
}

/**
 * AdminComments component allows administrators to view, add, and delete comments
 * related to a specific candidate. It fetches the comments from the server, displays
 * them in a list, and provides a form for adding new comments.
 *
 * @component
 * @param {AdminCommentsProps} props - The props for the AdminComments component.
 * @param {number} props.candidateId - The ID of the candidate for whom the comments are being managed.
 * @param {(date: string) => string} props.formatDateToReadable - A function to format dates into a readable string.
 * @param {(show: boolean) => void} props.setShowAdminComments - A function to toggle the visibility of the AdminComments component.
 *
 * @returns {JSX.Element} The rendered AdminComments component.
 *
 * @remarks
 * - This component uses Redux for state management and dispatches actions to fetch, add, and delete comments.
 * - It uses Formik for form handling and validation when adding new comments.
 * - The component includes a preloader for asynchronous actions and handles errors gracefully.
 *
 */
const AdminComments: React.FC<AdminCommentsProps> = ({
  candidateId,
  setShowAdminComments,
  setTotalAdminComments,
  totalAdminComments,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const commentsList = useAppSelector(
    (state) => state.superadmin.candidateAdminComments
  );
  const inProgress = useAppSelector((state) => state.superadmin.inProgress);
  const adminCommentsPostingProgress = useAppSelector(
    (state) => state.superadmin.adminCommentsPostingProgress
  );
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(
    null
  );

  /**
   * Gets the candidate comments when the component mounts.
   * It dispatches the `getCandidateComments` action to fetch the comments for the specified candidate ID.
   */
  useEffect(() => {
    const fetchAdminComments = async () => {
      await dispatch(getCandidateComments({ candidate_id: candidateId }));
    };

    fetchAdminComments();
  }, []);

  useEffect(() => {
    setTotalAdminComments &&
      setTotalAdminComments(
        commentsList?.comments?.length > 0 ? commentsList?.comments?.length : 0
      );
  }, [commentsList]);

  /**
   *
   * @param commentId - The ID of the comment to be deleted.
   * This function dispatches an action to delete the specified comment and updates the comments list.
   */
  const handleDeleteComment = async (commentId: number) => {
    if (commentId !== null) {
      dispatch(showTransparentPreloaderAction());
      setDeletingCommentId(commentId);
      const response = await dispatch(
        DeleteCandidateCommentsThunk({
          currentCandidateId: candidateId,
          commentId: commentId,
        })
      );
      if (isFulfilled(response)) {
        await dispatch(getCandidateComments({ candidate_id: candidateId }));
      }
      setDeletingCommentId(null);
      dispatch(hideTransparentPreloaderAction());
    }
  };

  /**
   * Custom hook using `useFormik` to manage form state and handle submission for admin comments.
   *
   */
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = useFormik<IAdminCommentsFormInput>({
    validationSchema: createValidationSchema("admincomments"),
    initialValues: {
      comments: "",
    },
    onSubmit: async (values: IAdminCommentsFormInput) => {
      try {
        dispatch(showTransparentPreloaderAction());
        const response = await dispatch(
          postCandidateCommentsThunk({
            currentCandidateId: candidateId,
            comment: values?.comments,
          })
        );
        if (isFulfilled(response)) {
          await dispatch(getCandidateComments({ candidate_id: candidateId }));
          const commentField = document.querySelector(
            "#comments"
          ) as HTMLTextAreaElement;
          if (commentField) {
            commentField.value = "";
            setFieldValue("comments", "");
            setFieldTouched("comments", false);
          }
          const commentsContainer = document.querySelector(
            ".overflow-y-auto"
          ) as HTMLDivElement;
          if (commentsContainer) {
            setTimeout(() => {
              commentsContainer.scrollTop = commentsContainer.scrollHeight;
            }, 0);
          }
        }
        dispatch(hideTransparentPreloaderAction());
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  /**
   * Conver the ISO date string to a readable format DD-MM-YYY.
   * @param isoDateString - The ISO date string to format.
   * @returns
   */
  const dateFormatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const formatDateToReadable = (isoDateString: string): string => {
    const date = new Date(isoDateString);

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };

    return new Intl.DateTimeFormat("en-GB", options).format(date).replace(",", "");
  };

  return (
    <>
      <div className="w-100 h-1px bg-grey-light"></div>
      <div className="d-flex flex-column gap-10">
        <div className="d-flex gap-20 align-items-center justify-content-between">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={() => setShowAdminComments(false)}
          >
            <LeftArrow />
            Back to Profile
          </CustomButton>
          <div className="fs-22 fw-400 text-black">Admin Comments</div>
        </div>
        <div className="overflow-y-auto max-h-150px d-flex flex-column gap-10 ">
          {commentsList?.comments?.map(
            (comment: ICommentResponse, index: number) => {
              return (
                <div
                  className="border border-off-white radius-8px p-20px d-flex flex-column gap-7"
                  key={index}
                >
                  <div className="fs-16 fw-400 text-black">
                    {comment?.comment}
                  </div>
                  <div className="d-flex gap-20 align-items-center justify-content-between">
                    <div className="fs-13 fw-400 text-grey-medium">
                      Comment by {comment?.author?.first_name}{" "}
                      {comment?.author?.last_name} on{" "}
                      {comment?.created_at !== null
                        ? formatDateToReadable(comment?.created_at)
                        : "N/A"}
                    </div>
                    <CustomButton
                      variant={"primary"}
                      type="button"
                      className="btn-theme bg-white text-black p-0 h-auto"
                      onClick={() => {
                        handleDeleteComment(comment?.id);
                      }}
                      // isLoading={deletingCommentId === comment?.id}
                      contentClass="d-flex"
                    >
                      <Delete />
                      Delete
                    </CustomButton>
                  </div>
                </div>
              );
            }
          )}
        </div>
        <div className="bg-teal-muted p-10px radius-8px d-flex flex-column gap-8 ">
          <div className="fs-12 fw-400 text-black">Add a Comment</div>
          <textarea
            className="form-control border-grey-medium"
            name="comments"
            id="comments"
            onChange={(e) => setFieldValue("comments", e.target.value)}
          ></textarea>
          {errors?.comments && touched?.comments && (
            <ErrorMessage message={errors.comments as string} />
          )}
          <div className="d-flex justify-content-end">
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
              isLoading={adminCommentsPostingProgress}
              contentClass="d-flex"
            >
              <RoundedTick
                color={adminCommentsPostingProgress ? "#aaaaaa" : "black"}
              />
              Post Comment
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminComments;
